import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../auth/auth.api";
import { TSuccessResponse } from "@/types/general/TSuccessResponse";
import TPaginationParams from "@/types/general/TPaginationParams";
import TEmployee from "@/types/employee/TEmployee";
import TKPI from "@/types/kpi-management/TKPI";
import TEmployeeKPI from "@/types/kpi-management/TEmployeeKPI";

export const employeeApi = createApi({
  reducerPath: "employeeApi",
  tagTypes: ["employee"],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getAllEmployee: builder.query<
      TSuccessResponse<TEmployee[]>,
      {
        pagination?: TPaginationParams;
        searchKey?: string;
        filter?: {
          position_id: number;
          departments: number[];
          user_id: number | string | null;
        };
      }
    >({
      query: ({ pagination = {} as any, searchKey, filter }) => {
        const params: Record<any, any> = {};
        const { page = 1, limit = 100 } = pagination;

        if (page && limit) {
          params.page = page;
          params.limit = limit;
        }

        if (searchKey) {
          params.search = searchKey;
        }

        if (filter?.position_id) {
          params.position_id = filter.position_id;
        }
        if (filter?.user_id) {
          params.user_id = filter.user_id;
        }

        if (filter?.departments && filter.departments.length > 0) {
          params.departments = filter.departments.join(", ");
        }

        return {
          url: "employee-management/employee",
          params: params,
        };
      },
    }),

    getEmployee: builder.query<TSuccessResponse<TEmployee>, string>({
      query: (employeeId) => {
        return {
          url: `employee-management/employee/${employeeId}`,
        };
      },
    }),

    getEmployeeKpiList: builder.query<
      TSuccessResponse<Array<TEmployeeKPI>>,
      { employeeId: number; year?: string | number; month?: string | number }
    >({
      query: ({ employeeId, year = undefined, month = undefined }) => {
        return {
          url: `employee-management/employee/${employeeId}/kpis`,
          params: { year, month },
        };
      },
    }),

    updateEmployeeKpi: builder.mutation<
      TSuccessResponse<null>,
      { userId: number; data: TEmployeeKPI }
    >({
      query: ({ userId, data }) => ({
        url: `employee-management/employee/${userId}/kpis`,
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useGetAllEmployeeQuery,
  useGetEmployeeQuery,
  useGetEmployeeKpiListQuery,
  useUpdateEmployeeKpiMutation,
} = employeeApi;
